// PrivacyPolicyPage.js
import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Divider,
  Link,
  useColorModeValue,
  Button,
  Select,
} from "@chakra-ui/react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";


const PrivacyPolicyPage = () => {
  const { t, i18n } = useTranslation();

  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const linkColor = useColorModeValue("blue.500", "blue.300");

  const navigate = useNavigate();

  // Handle language change
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  return (
    <Box bg={useColorModeValue("gray.50", "gray.900")} minH="100vh" py={10}>
      {/* Language Switcher */}
      <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>
      <Container
        maxW="container.md"
        bg={useColorModeValue("white", "gray.800")}
        p={8}
        borderRadius="lg"
        boxShadow="xl"
      >
        <VStack spacing={6} align="stretch">
          <Heading size="2xl" color={headingColor} textAlign="center">
            {t("privacy_policy_page.title")}
          </Heading>
          <Text fontSize="md" color={textColor} textAlign="center">
            {t("privacy_policy_page.effective_date")}
          </Text>
          <Divider />

          <Text fontSize="md" color={textColor}>
            {t("privacy_policy_page.intro.welcome")}{" "}
            <Link as={NavLink} to="/terms-of-use" color={linkColor}>
              {t("privacy_policy_page.intro.terms_of_use_link")}
            </Link>.
          </Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.information_we_collect.title")}
          </Heading>
          <Text color={textColor} fontWeight="bold">
            {t("privacy_policy_page.information_we_collect.direct_info.title")}
          </Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.direct_info.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.direct_info.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.direct_info.description_3")}</Text>

          <Text color={textColor} fontWeight="bold">
            {t("privacy_policy_page.information_we_collect.auto_collected_info.title")}
          </Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.auto_collected_info.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.auto_collected_info.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.auto_collected_info.description_3")}</Text>

          <Text color={textColor} fontWeight="bold">
            {t("privacy_policy_page.information_we_collect.third_party_info.title")}
          </Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.third_party_info.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.third_party_info.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.information_we_collect.third_party_info.description_3")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.how_we_use_your_information.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.how_we_use_your_information.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.how_we_use_your_information.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.how_we_use_your_information.description_3")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.how_we_share_your_information.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.how_we_share_your_information.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.how_we_share_your_information.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.how_we_share_your_information.description_3")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.data_security.title")}
          </Heading>
          <Text fontSize="md" color={textColor}>{t("privacy_policy_page.data_security.description_1")}</Text>
          <Text fontSize="md" color={textColor}>{t("privacy_policy_page.data_security.description_2")}</Text>
          <Text fontSize="md" color={textColor}>{t("privacy_policy_page.data_security.description_3")}</Text>
          <Text fontSize="md" color={textColor}>{t("privacy_policy_page.data_security.description_4")}</Text>
          <Text fontSize="md" color={textColor}>{t("privacy_policy_page.data_security.description_5")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.data_retention.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.data_retention.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.data_retention.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.data_retention.description_3")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.data_retention.description_4")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.data_retention.description_5")}</Text>


          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.your_rights_choices.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.your_rights_choices.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.your_rights_choices.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.your_rights_choices.description_3")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.your_rights_choices.description_4")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.your_rights_choices.description_5")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.cookies_tracking.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.cookies_tracking.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.cookies_tracking.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.cookies_tracking.description_3")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.cookies_tracking.description_4")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.cookies_tracking.description_5")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.third_party_links_services.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.third_party_links_services.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.third_party_links_services.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.third_party_links_services.description_3")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.third_party_links_services.description_4")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.third_party_links_services.description_5")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.international_data_transfers.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.international_data_transfers.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.international_data_transfers.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.international_data_transfers.description_3")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.international_data_transfers.description_4")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.international_data_transfers.description_5")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.changes_to_policy.title")}
          </Heading>
          <Text color={textColor}>{t("privacy_policy_page.changes_to_policy.description_1")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.changes_to_policy.description_2")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.changes_to_policy.description_3")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.changes_to_policy.description_4")}</Text>
          <Text color={textColor}>{t("privacy_policy_page.changes_to_policy.description_5")}</Text>

          <Heading size="lg" color={headingColor}>
            {t("privacy_policy_page.contact_information.title")}
          </Heading>
          <Text color={textColor}>
            {t("privacy_policy_page.contact_information.description_1")}
          </Text>
          <Text color={textColor}>
            {t("privacy_policy_page.contact_information.description_2")}
          </Text>
          <Text color={textColor}>
            📧 <Link href="mailto:support@peerx.ca" color={linkColor}>{t("privacy_policy_page.contact_information.email")}</Link>
          </Text>
          <Text color={textColor}>
            📍 {t("privacy_policy_page.contact_information.address")}
          </Text>
          <Text color={textColor}>
            {t("privacy_policy_page.contact_information.description_3")}
          </Text>

          {/* Back to Home Button */}
          <Button colorScheme="blue" variant="solid" onClick={() => navigate("/")}>
            {t("privacy_policy_page.back_to_home")}
          </Button>

          <Text color={textColor}>
            {t("privacy_policy_page.thank_you")}
          </Text>


        </VStack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
